import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import UserContext from '../../context/UserContext'

const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });


/* ---------- LINKIFY HELPERS ---------- */
const LINK_TOKEN = /((https?:\/\/|www\.)[^\s<]+|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/gi;
const TRAIL_PUNCT = /[),.!?;:]+$/; // punctuation we want to keep outside the link

function linkifyLine(line) {
  const out = [];
  let lastIndex = 0;
  let match;

  while ((match = LINK_TOKEN.exec(line)) !== null) {
    const start = match.index;
    const raw = match[0];

    // push text before the match
    if (start > lastIndex) {
      out.push(line.slice(lastIndex, start));
    }

    // strip trailing punctuation from the clickable part
    const punctMatch = raw.match(TRAIL_PUNCT);
    const trailing = punctMatch ? punctMatch[0] : '';
    const core = trailing ? raw.slice(0, -trailing.length) : raw;

    // decide href
    let href;
    if (core.includes('@') && !core.startsWith('www.')) {
      href = `mailto:${core}`;
    } else {
      href = core.startsWith('www.') ? `https://${core}` : core;
    }

    out.push(
      <a 
        key={`lnk-${start}`}
        href={href} style={{color:"#fff"}}
        target="_blank"
        rel="noopener noreferrer"
        className="ecd-link"
      >
        {core}
      </a>
    );

    // push any trailing punctuation back as plain text
    if (trailing) out.push(trailing);

    lastIndex = start + raw.length;
  }

  // tail after last match
  if (lastIndex < line.length) {
    out.push(line.slice(lastIndex));
  }

  return out;
}

function linkifyMultiline(text) {
  if (text == null) return null;
  const lines = String(text).split('\n');
  return lines.map((line, i) => (
    <React.Fragment key={`ln-${i}`}>
      {linkifyLine(line)}
      {i < lines.length - 1 ? <br /> : null}
    </React.Fragment>
  ));
}

/*------*/

function parseUtcToDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value); // epoch ms
  if (typeof value === 'string') {
    // ISO -> Date; or "YYYY-MM-DD HH:mm:ss" (UTC) -> ISO
    const iso = value.includes('T') || value.endsWith('Z')
      ? value
      : value.replace(' ', 'T') + 'Z';
    const t = Date.parse(iso);
    return Number.isFinite(t) ? new Date(t) : null;
  }
  return null;
}

function relativeLabel(utcValue, nowMs) {
  const d = parseUtcToDate(utcValue);
  if (!d) return '';
  const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
  const abs = Math.abs(diffSec);

  if (abs < 60) return rtf.format(-diffSec, 'second');
  const min = Math.round(diffSec / 60);
  if (Math.abs(min) < 60) return rtf.format(-min, 'minute');
  const hr = Math.round(min / 60);
  if (Math.abs(hr) < 24) return rtf.format(-hr, 'hour');
  const day = Math.round(hr / 24);
  if (Math.abs(day) < 30) return rtf.format(-day, 'day');
  const month = Math.round(day / 30);
  if (Math.abs(month) < 12) return rtf.format(-month, 'month');
  const year = Math.round(month / 12);
  return rtf.format(-year, 'year');
}

function toISO(utcValue) {
  const d = parseUtcToDate(utcValue);
  return d ? d.toISOString() : '';
}

function formatLocalTooltip(utcValue) {
  const d = parseUtcToDate(utcValue);
  if (!d) return '';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  }).format(d); // user’s local TZ
}

export default function RestofChatScrollable() {
  const { listofMessages } = useContext(UserContext);
  const allContext = useContext(UserContext);
  const chatLogRef = useRef(null);

  // refresh relative times
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(id);
  }, []);

  const isNearBottom = () => {
    const el = chatLogRef.current;
    if (!el) return true;
    const threshold = 80; // px from bottom considered "near"
    return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
  };

  const scrollToBottom = () => {
    const el = chatLogRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  // When message count changes, if user is near bottom, scroll after layout/paint.
  const msgCount = listofMessages?.length || 0;
  useLayoutEffect(() => {
    if (!isNearBottom()) return;
    // double RAF to let React commit + browser layout + images sizing settle
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    });
  }, [msgCount]);

  // Also scroll when an avatar finishes loading (in case image height affects layout)
  const handleImageLoad = () => {
    if (isNearBottom()) scrollToBottom();
  };

  const agent_is_typing = allContext.agent_is_typing;

  const listMyMessages = () => {
    if (!listofMessages || listofMessages.length === 0) {
      return <p style={{ color: 'white' }}>You can start typing your message..</p>;
    }

    return listofMessages.map((item, index) => {
      const isAgent = item.sender === 'agent' || item?.who_sent_message === 'agent';
      const avatar_url = (isAgent
        ? item.avatar_url
        : 'https://cdn.easychatdesk.com/images/uu.png'
      ) || 'https://easychatdesk.com/images/avatar.png';

      const rel = relativeLabel(item.datemade, now);
      const iso = toISO(item.datemade);
      const tooltip = formatLocalTooltip(item.datemade);

      if (isAgent) {
        return (
          <div key={index} className="chat_itm">
            <div className="chat-item-from-list">
              <div className="chat_message2-dark">{linkifyMultiline(item.message)}</div>
              <time dateTime={iso} title={tooltip} style={{ color: '#888', fontSize: 11 }}>
                {rel || 'just now'}
              </time>
            </div>
            <div style={{ paddingLeft: 10 }}>
              <img
                src={avatar_url}
                className="messagesAvatar"
                style={{ height: 36, width: 36 }}
                alt=""
                onLoad={handleImageLoad}
              />
            </div>
          </div>
        );
      }

      return (
        <div key={index} className="chat_itm">
          <div style={{ paddingRight: 10 }}>
            <img
              src={avatar_url}
              className="messagesAvatar"
              style={{ height: 36, width: 36 }}
              alt=""
              onLoad={handleImageLoad}
            />
          </div>
          <div className="chat-item-from-list">
            <div className="chat_message-dark">{linkifyMultiline(item.message)}</div>
            <time dateTime={iso} title={tooltip} style={{ color: '#888', fontSize: 11 }}>
              {rel || 'just now'}
            </time>
          </div>
        </div>
      );
    });
  };

  const displayAgentTyping = () => agent_is_typing
    ? <div className="agent-typing-message">Agent is typing...</div>
    : null;

  return (
    <div ref={chatLogRef} className="ecd-RestofChatScrollable">
      {listMyMessages()}
      {displayAgentTyping()}
    </div>
  );
}